import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SERVICES, type RequestDto } from '@shared';
import { apiGet, ApiError } from '../../lib/api';
import { StatusChip, StatusBar } from '../../components/StatusChip';
import { MessageThread } from '../../components/MessageThread';
import { useAuth } from '../../lib/auth-context';

interface ProposalLite { id: string; status: string }
interface InvoiceLite { id: string; status: string; amount_pence: number; currency: string }
interface DocumentLite { id: string; title: string; kind: string }

export function RequestDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [req, setReq] = useState<RequestDto | null>(null);
  const [proposals, setProposals] = useState<ProposalLite[]>([]);
  const [invoices, setInvoices] = useState<InvoiceLite[]>([]);
  const [documents, setDocuments] = useState<DocumentLite[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    apiGet<{ request: RequestDto }>(`/api/requests/${id}`)
      .then((r) => setReq(r.request))
      .catch((err) => setError(err instanceof ApiError ? err.serverMessage : 'Could not load brief.'));
    // Linked artifacts. Soft-fail — if any fail, the detail page still renders.
    apiGet<{ proposals: ProposalLite[] }>(`/api/proposals?request_id=${encodeURIComponent(id)}`)
      .then((r) => setProposals(r.proposals)).catch(() => undefined);
    apiGet<{ invoices: InvoiceLite[] }>(`/api/invoices?request_id=${encodeURIComponent(id)}`)
      .then((r) => setInvoices(r.invoices)).catch(() => undefined);
    apiGet<{ documents: DocumentLite[] }>(`/api/documents?request_id=${encodeURIComponent(id)}`)
      .then((r) => setDocuments(r.documents)).catch(() => undefined);
  }, [id]);

  if (error) {
    return (
      <>
        <h1 className="portal-h1">Brief not found</h1>
        <p className="portal-sub">{error}</p>
        <Link className="btn ghost" to="/portal/requests" style={{ marginTop: 24 }}>← All requests</Link>
      </>
    );
  }

  if (!req) {
    return <p className="portal-sub">Loading…</p>;
  }

  const svc = SERVICES.find((s) => s.id === req.service);

  return (
    <>
      <div style={{ fontSize: 13, color: 'var(--fg-mute)', marginBottom: 8 }}>
        <Link to="/portal/requests" style={{ borderBottom: '1px solid var(--rule)' }}>My briefs</Link>
        {' / '}<span className="rid">{req.id}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
        <div>
          <h1 className="portal-h1">{req.headline}</h1>
          <p className="portal-sub">
            <span className="rid">{req.id}</span> · {svc?.name ?? req.service} · submitted {req.created_at.slice(0, 10)}
          </p>
        </div>
        <StatusChip status={req.status} />
      </div>

      <div style={{ marginTop: 28 }}>
        <StatusBar status={req.status} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginTop: 32 }}>
        <div className="card">
          <h3 style={{ fontSize: 17 }}>Brief</h3>
          <p style={{ color: 'var(--fg-soft)', fontSize: 14, lineHeight: 1.6, marginTop: 12, whiteSpace: 'pre-wrap' }}>
            {req.description}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <h3 style={{ fontSize: 17 }}>Engagement</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14, fontSize: 13 }}>
              <div style={{ borderBottom: '1px solid var(--rule)', paddingBottom: 10 }}>
                <div style={{ color: 'var(--fg-mute)', marginBottom: 4 }}>Engagement lead</div>
                {req.assigned_lead ? (
                  <>
                    <div style={{ fontWeight: 500 }}>{req.assigned_lead.name}</div>
                    <div style={{ color: 'var(--fg-mute)', fontSize: 12, marginTop: 2 }}>
                      {req.assigned_lead.role}
                      {req.assigned_lead.office ? ` · ${req.assigned_lead.office}` : ''}
                    </div>
                  </>
                ) : (
                  <div style={{ color: 'var(--fg-soft)' }}>Not yet assigned</div>
                )}
              </div>
              <Row k="Urgency"               v={req.urgency} />
              <Row k="Counterparty"          v={req.counterparty || '—'} />
              <Row k="Indicative fee"        v={svc?.fee ?? '—'} />
              <Row k="Indicative turnaround" v={svc?.lead ?? '—'} />
              <Row k="Engagement value"      v={req.value_pence ? `£${(req.value_pence / 100).toLocaleString()}` : 'TBD'} />
            </div>
          </div>

          {(proposals.length > 0 || invoices.length > 0 || documents.length > 0) && (
            <div className="card">
              <h3 style={{ fontSize: 17 }}>Linked items</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12, fontSize: 13 }}>
                {proposals.map((p) => (
                  <Link key={p.id} to={`/portal/proposals/${p.id}`} style={{ display: 'flex', justifyContent: 'space-between', textDecoration: 'none', color: 'inherit' }}>
                    <span>Proposal <span className="rid">{p.id}</span></span>
                    <span className="chip">{p.status}</span>
                  </Link>
                ))}
                {invoices.map((i) => (
                  <Link key={i.id} to={`/portal/invoices/${i.id}`} style={{ display: 'flex', justifyContent: 'space-between', textDecoration: 'none', color: 'inherit' }}>
                    <span>Invoice <span className="rid">{i.id}</span></span>
                    <span className="chip">{i.status}</span>
                  </Link>
                ))}
                {documents.map((d) => (
                  <Link key={d.id} to="/portal/documents" style={{ display: 'flex', justifyContent: 'space-between', textDecoration: 'none', color: 'inherit' }}>
                    <span>{d.title}</span>
                    <span className="chip" style={{ fontSize: 10 }}>{d.kind}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <h3 style={{ fontSize: 17, marginBottom: 14 }}>Messages</h3>
        {user ? (
          <MessageThread requestId={req.id} perspective="client" currentUserId={user.id} />
        ) : (
          <p className="portal-sub">Sign in to view the thread.</p>
        )}
      </div>
    </>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--rule)', paddingBottom: 8 }}>
      <span style={{ color: 'var(--fg-mute)' }}>{k}</span>
      <span>{v}</span>
    </div>
  );
}
